import { colors } from '@resiliatech/birdsong'
import {
  GoalGroupChartData,
  GoalGroupChartSeries,
} from 'Generated/graphql-types'
import { funderGoals } from 'Modules/Analytics/trackActionInterface/FunderPlatform/funderGoals'
import { SponsoredNonprofits } from 'TypeDefs/AppContext'
import { GoalGroupFields } from './types'

export const resiliaPalette = [
  colors.visualization.magenta,
  colors.visualization.tintGreen,
  colors.visualization.cobalt,
  colors.visualization.tintMagenta,
  colors.accent.lightBlue,
  colors.visualization.tintBrown,
  colors.accent.lightOrange,
  colors.visualization.tintTeal,
  colors.visualization.indigo,
  colors.visualization.tintIndigo,
  colors.visualization.teal,
  colors.visualization.tintCobalt,
  colors.feedback.infoDark,
  colors.visualization.tintBurgundy,
  colors.accent.lightGreen,
  colors.feedback.infoLight,
]

export const ADD_GOAL_DISABLED_COPY = 'Goal requested!'

export const truncateGoalName = (name: string, maxLength = 35) => {
  if (name.length <= maxLength) return name
  return `${name.slice(0, maxLength - 3)}...`
}

export const formatLargeNumber = (value: number): string => {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}k`
  }
  return value.toString()
}

export const formatValue = (
  series: GoalGroupChartSeries,
  value: number | null
) => {
  if (value === null) return 'No data'
  const date = series.data.find((p) => p.y === value)?.x
  return `${series.nonprofitOrgName}:${value}:${date}:${series.goalName}`
}

export const truncateNonprofitOrgName = (name: string) =>
  name.length > 40 ? `${name.slice(0, 20)}…` : name

const getDataPointForLabel = (series: GoalGroupChartSeries, label: string) => {
  const point = series.data.find((p) => p.x === label)
  return point ? point.y : null
}

export const createFormattedSeriesData = (
  chartData: GoalGroupChartData,
  xLabels: string[]
) => {
  if (!chartData) return []

  return chartData.seriesData.map(
    (series: GoalGroupChartSeries, index: number) => {
      let totalSeriesValue = 0
      const cumulativeSeriesData: any = {
        ...series,
        data: series.data.map(
          (point) => {
            totalSeriesValue += point?.y || 0
            return {
              x: point?.x,
              y: totalSeriesValue,
            }
          },
          { x: series.data[0]?.x, y: series.data[0]?.y }
        ),
      }

      return {
        type: 'line',
        curve: 'natural',
        data: xLabels.map((label) =>
          getDataPointForLabel(cumulativeSeriesData, label)
        ),
        id: `series-${index}`,
        label: truncateNonprofitOrgName(series.nonprofitOrgName),
        color: resiliaPalette[index % resiliaPalette.length],
        showMark: true,
        connectNulls: true,
        valueFormatter: (value: number | null) => formatValue(series, value),
      }
    }
  )
}

export const createAggregateSeriesData = (
  chartData: GoalGroupChartData,
  xLabels: string[]
) => {
  if (!chartData || !chartData.seriesData) return []

  let runningTotal = 0
  const aggregatedData = xLabels.map((label) => {
    const sum = chartData.seriesData.reduce(
      (acc: number, series: GoalGroupChartSeries) => {
        const point = series.data.find((p) => p.x === label)
        return acc + (point && point.y != null ? point.y : 0)
      },
      0
    )
    runningTotal += sum
    return runningTotal
  })

  return [
    {
      type: 'line',
      curve: 'natural',
      data: aggregatedData,
      id: 'aggregated-series',
      label: 'Aggregated Data',
      color: resiliaPalette[0],
      showMark: true,
      connectNulls: true,
      valueFormatter: (value: number | null) =>
        value === null
          ? 'No data'
          : `${chartData.title}:${value}:${
              xLabels[aggregatedData.indexOf(value)]
            }:Aggregated data from all Grantees`,
    },
  ]
}

export const sendFunderGoalTabClickedEvent = (
  tabName: string,
  goalGroupId: number,
  userId: number,
  userEmail: string,
  funderOrgId: number,
  nonprofitOrgIds: number[],
  goalNames: string[]
) => {
  const collabGoalInfo = {
    goalGroupId,
    funderUserInfo: {
      userId,
      userEmail,
      funderOrgId,
    },
    nonprofitOrgIds,
    goalNames,
  }

  analytics.track(
    `Funder_Collab_Goal_${tabName}_Tab_Clicked`,
    funderGoals.Funder_Collab_Goal_Tab_Clicked.Funder_Collab_Goal_Tab_Clicked(
      collabGoalInfo
    )
  )
}

export const sendFunderGoalChartViewedEvent = (
  chartFilterValue: string,
  chartType: string,
  goalGroupId: number | null,
  userId: number,
  userEmail: string,
  funderOrgId: number
) => {
  const eventData = {
    chartFilterValue,
    chartType,
    collabGoalInfo: {
      goalGroupId,
      funderUserInfo: {
        userId,
        userEmail,
        funderOrgId,
      },
    },
  }

  analytics.track(
    `Funder_Collab_Goal_${chartType}_Filter_Clicked`,
    funderGoals.Funder_Collab_Goal_Chart_Filter_Clicked.Funder_Collab_Goal_Chart_Filter_Clicked(
      eventData
    )
  )
}

export const sendCollabChartToggleEvent = (
  chartType: string,
  userId: number,
  userEmail: string,
  funderOrgId: number,
  goalId: number,
  toggleLabel: string
) => {
  const eventData = {
    chartType,
    goalId,
    toggleLabel,
    funderUserInfo: {
      userId,
      userEmail,
      funderOrgId,
    },
  }

  analytics.track(
    `Funder_Collab_Toggle_${toggleLabel}_Clicked`,
    funderGoals.Funder_Collab_Goal_Chart_Toggle_Clicked.Funder_Collab_Goal_Chart_Toggle_Clicked(
      eventData
    )
  )
}

export const getUpdatedGoalGroupFields = (
  currentValues: Partial<GoalGroupFields>,
  newValues: Partial<GoalGroupFields>
): string[] => {
  try {
    const updatedFields: string[] = []

    if (currentValues.title !== newValues.title) updatedFields.push('title')
    if (currentValues.description !== newValues.description)
      updatedFields.push('description')
    if (currentValues.category !== newValues.category)
      updatedFields.push('category')
    if ((currentValues.startDate || null) !== (newValues.startDate || null)) {
      updatedFields.push('startDate')
    }
    if ((currentValues.endDate || null) !== (newValues.endDate || null)) {
      updatedFields.push('endDate')
    }
    const currentTarget =
      currentValues.target === 'null' ? '' : currentValues.target
    const newTarget = newValues.target === null ? '' : newValues.target
    if (currentTarget !== newTarget) {
      updatedFields.push('target')
    }
    if (currentValues.targetDescription !== newValues.targetDescription)
      updatedFields.push('targetDescription')

    return updatedFields
  } catch (error) {
    return []
  }
}

export const GOAL_PAGE_SUBHEADER_TEXT = `Data reflects impact across this cohort. Reach out to your Customer Success Manager (CSM) if you need additional insights.`

export const getNonprofitName = (
  sponsoredNonprofitsList: SponsoredNonprofits,
  goalNonprofitOrgId: number | undefined | null
): string | null => {
  if (goalNonprofitOrgId == null) return null
  const nonprofit = sponsoredNonprofitsList[goalNonprofitOrgId.toString()]
  return nonprofit?.name ?? null
}
